import { RepeatIcon, SearchIcon } from '@chakra-ui/icons';
import DataTable from 'react-data-table-component';

import axios from 'axios';
import debounce from 'lodash.debounce';
import { useEffect, useState } from 'react';
import { AsyncSelect, Select } from 'chakra-react-select';

import {
  Box,
  Text,
  Flex,
  VStack,
  Divider,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  TableCaption,
  IconButton,
  InputGroup,
  InputRightElement,
  Input,
  Spacer,
  Badge,
  useToast,
  useColorModeValue,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { SiMicrosoftexcel } from 'react-icons/si';

import FormikFormControl from 'components/common/forms/FormikFormControl';
import TableItemLoader from 'components/common/tables/TableItemLoader';

import {
  COMPLETED,
  IN_PROGRESS,
  NOT_STARTED,
} from 'utils/constants/reportStatus';

import { useSelector, useDispatch } from 'react-redux';
import { ReducerStatus } from 'utils/constants';
import { getCompletionReports } from 'store/modules/report/report-action';
import { useMsal } from '@azure/msal-react';
import { completionReportColumns, getReportSummary } from 'utils/reportHelper';
import exportAsCsv from 'utils/exportCsv';
import { ADMIN, TRAINER } from 'utils/constants/roles';

function ReportPage() {
  document.title = `Skill Quest | Completion Report`;

  const functionCodeOptions = [
    { label: 'IQ', value: 'IQ' },
    { label: 'SG', value: 'SG' },
    { label: 'TR', value: 'TR' },
    { label: 'TSCONS', value: 'TSCONS' },
    { label: 'TSCORP', value: 'TSCORP' },
  ];

  const { accounts } = useMsal();
  const { idTokenClaims, name } = accounts[0];
  const { roles } = idTokenClaims;

  const toast = useToast();
  const dispatch = useDispatch();

  const [functionCodes, setFunctionCodes] = useState(functionCodeOptions);
  const [countryCodes, setCountryCodes] = useState([]);
  const [countryCodeOptions, setCountryCodeOptions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [managers, setManagers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isError, setIsError] = useState(false);
  const [isManagerMode, setIsManagerMode] = useState(false);

  const completionReports = useSelector(
    (state) => state.report.completionReports
  );
  const status = useSelector((state) => state.report.getCompletionReportStatus);

  const filteredReports = completionReports.filter(
    (item) =>
      item.name && item.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleChange = (setter) => (selectedOptions) => {
    setter(selectedOptions);
  };

  const handleFunctionCodeChange = handleChange(setFunctionCodes);
  const handleCountryCodeChange = handleChange(setCountryCodes);
  const handleOrganizationChange = handleChange(setOrganizations);
  const handleManagerChange = handleChange(setManagers);
  const handleCourseChange = handleChange(setCourses);

  const handleResetFields = () => {
    setFunctionCodes(functionCodeOptions);

    // Reset country codes to PHL default
    const phlOption = countryCodeOptions.find(
      (option) => option.value === 'PHL'
    );
    setCountryCodes(phlOption ? [phlOption] : []);

    setOrganizations([]);
    setManagers([]);
    setCourses([]);
  };

  useEffect(() => {
    if (courses.length > 0) setIsError(false);
  }, [courses]);

  // Load country codes once when component mounts
  useEffect(() => {
    axios
      .get('/users/country-codes')
      .then((response) => {
        const options = response.data.map((code) => ({
          label: code,
          value: code,
        }));
        setCountryCodeOptions(options);

        // Set PHL as default selection if it exists in the options
        const phlOption = options.find((option) => option.value === 'PHL');
        if (phlOption) {
          setCountryCodes([phlOption]);
        }
      })
      .catch(() => {
        // Handle error silently or show toast if needed
      });
  }, []);

  // Load organizations once when component mounts
  useEffect(() => {
    axios
      .get('/users/organizations')
      .then((response) => {
        const options = response.data.map((org) => ({
          label: org.name || org,
          value: org.id || org,
        }));
        setOrganizationOptions(options);
      })
      .catch(() => {
        // Handle error silently or show toast if needed
      });
  }, []);

  useEffect(() => {
    if (
      roles.includes('Manager') &&
      !roles.includes(TRAINER) &&
      !roles.includes(ADMIN)
    )
      setIsManagerMode(true);
  }, [roles]);

  useEffect(() => {
    if (status === ReducerStatus.FAILED)
      toast({
        title: 'Error',
        description: 'Something went wrong with the request',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
  }, [status, toast]);

  const loadManagers = debounce((inputValue, callback) => {
    axios
      .get(
        `/users?query=${inputValue.trim()}&pageSize=${5}&isSupervisor=${true}`
      )
      .then((response) => {
        callback(
          response.data.data?.map((user) => ({
            label: user.fullName,
            value: user.id,
          }))
        );
      })
      .catch(() => {
        callback([]);
      });
  }, 1000);

  const loadCourses = debounce((inputValue, callback) => {
    axios
      .get(`/courses?query=${inputValue.trim()}&pageSize=${5}`)
      .then((response) => {
        callback(
          response.data.data?.map((courseItem) => ({
            label: courseItem.name,
            value: courseItem.id,
          }))
        );
      })
      .catch(() => {
        callback([]);
      });
  }, 1000);

  const handleGenerate = async () => {
    if (courses.length === 0) {
      setIsError(true);
      return;
    }

    dispatch(
      getCompletionReports({
        courses,
        managers,
        functionCodes,
        countryCodes,
        organizations,
      })
    );
  };

  /**
   * Handles CSV export of completion reports with calculated percentage column
   * Adds completion percentage to each record before export
   */
  const handleExport = () => {
    // Function to add percentage calculation and EXT- ID prefix to report data
    const addPercentageToReports = (reports) => {
      return reports.map((report) => ({
        ...report,
        id: report.id < 0 ? `EXT${report.id}` : report.id,
        completionPercentage:
          report.totalActivities > 0
            ? Math.round(
                (report.completedActivities / report.totalActivities) * 100
              )
            : 0,
      }));
    };

    // Export selected data or all data with percentage column
    if (selectedData.length > 0) {
      const dataWithPercentage = addPercentageToReports(selectedData);
      exportAsCsv(dataWithPercentage, 'course-completion-report');
    } else {
      const dataWithPercentage = addPercentageToReports(completionReports);
      exportAsCsv(dataWithPercentage, 'course-completion-report');
    }
  };

  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
  };

  return (
    <Box minH="100vh" ml={{ base: 0, md: 20 }} p="10">
      <Text my={4} fontSize="2xl">
        Completion Reports
      </Text>

      <Box p={4} boxShadow="md" borderWidth="1px" borderRadius="lg">
        <Flex
          justify="streched"
          direction={{ base: 'column', lg: 'row' }}
          gap={3}
        >
          <Box flex={1}>
            <Text as="samp" fontSize="xs">
              <strong>SELECT FILTER</strong>
            </Text>
            <VStack mt={4} spacing={6} align="stretch">
              <Flex
                gap={5}
                direction={{
                  base: 'column',
                  xl: 'row',
                }}
              >
                <FormikFormControl label="Function Code" helperText="">
                  <Select
                    placeholder="Select function code"
                    value={functionCodes}
                    onChange={handleFunctionCodeChange}
                    options={functionCodeOptions}
                    isMulti
                    isClearable
                  />
                </FormikFormControl>

                <FormikFormControl label="Country Code" helperText="">
                  <Select
                    placeholder="Select country codes"
                    options={countryCodeOptions}
                    value={countryCodes}
                    onChange={handleCountryCodeChange}
                    isMulti
                    isClearable
                    isSearchable
                  />
                </FormikFormControl>
              </Flex>

              <FormikFormControl
                label="Organization"
                helperText="Example: PH Business Applications"
              >
                <Select
                  placeholder="Select organizations"
                  options={organizationOptions}
                  value={organizations}
                  onChange={handleOrganizationChange}
                  isMulti
                  isClearable
                  isSearchable
                />
              </FormikFormControl>

              <FormikFormControl label="Manager" helperText="">
                <AsyncSelect
                  isDisabled={isManagerMode}
                  placeholder={
                    isManagerMode ? `${name} (You)` : 'Search manager'
                  }
                  loadOptions={loadManagers}
                  value={managers}
                  onChange={handleManagerChange}
                  isMulti
                  isClearable
                />
              </FormikFormControl>

              <FormikFormControl
                label="Courses"
                helperText=""
                errors={isError === true ? 'Course is required' : 'test'}
                touched={isError}
              >
                <AsyncSelect
                  placeholder="Search courses"
                  loadOptions={loadCourses}
                  value={courses}
                  onChange={handleCourseChange}
                  isMulti
                  isClearable
                />
              </FormikFormControl>

              <Flex justify="right" gap={5}>
                <Button
                  onClick={handleResetFields}
                  colorScheme="blue"
                  variant="outline"
                  px={10}
                >
                  Reset
                </Button>
                <Button
                  colorScheme="red"
                  px={10}
                  isLoading={status === ReducerStatus.LOADING}
                  onClick={handleGenerate}
                >
                  Generate
                </Button>
              </Flex>
            </VStack>
          </Box>

          <Box height="100%">
            <Divider orientation="vertical" />
          </Box>

          <Box w={{ base: '100%', lg: '50%' }}>
            <Text as="samp" fontSize="xs">
              <strong> REPORT SUMMARY</strong>
            </Text>
            <TableContainer mt={4}>
              <Table size="sm">
                {completionReports.length > 0 && (
                  <TableCaption>
                    course(s) activities: {completionReports[0].totalActivities}
                  </TableCaption>
                )}

                <Thead>
                  <Tr>
                    <Th>type</Th>
                    <Th>percentage</Th>
                    <Th isNumeric>count</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {status === ReducerStatus.LOADING ? (
                    <TableItemLoader numberOfRows={3} numberOfColumns={3} />
                  ) : (
                    <>
                      <Tr>
                        <Td>
                          <Badge colorScheme="green">{COMPLETED}</Badge>
                        </Td>
                        <Td>
                          {
                            getReportSummary(completionReports, COMPLETED)
                              .percentage
                          }
                          %
                        </Td>
                        <Td isNumeric>
                          {getReportSummary(completionReports, COMPLETED).count}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td>
                          <Badge colorScheme="blue">{IN_PROGRESS}</Badge>
                        </Td>
                        <Td>
                          {
                            getReportSummary(completionReports, IN_PROGRESS)
                              .percentage
                          }
                          %
                        </Td>
                        <Td isNumeric>
                          {
                            getReportSummary(completionReports, IN_PROGRESS)
                              .count
                          }
                        </Td>
                      </Tr>
                      <Tr>
                        <Td>
                          <Badge colorScheme="gray">{NOT_STARTED}</Badge>
                        </Td>
                        <Td>
                          {
                            getReportSummary(completionReports, NOT_STARTED)
                              .percentage
                          }
                          %
                        </Td>
                        <Td isNumeric>
                          {
                            getReportSummary(completionReports, NOT_STARTED)
                              .count
                          }
                        </Td>
                      </Tr>
                    </>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </Flex>
      </Box>

      <Box mt={5} w="100%" p={4} color="gray.500" borderWidth="1px">
        <Flex align="center" m={2}>
          <Button
            onClick={handleExport}
            rightIcon={<SiMicrosoftexcel />}
            size="sm"
            colorScheme="blue"
            variant="outline"
          >
            Export
          </Button>
          <Spacer />
          <IconButton
            onClick={handleGenerate}
            aria-label="Reload table"
            variant="link"
            icon={<RepeatIcon />}
          />

          <InputGroup width={{ base: '100%', md: '350px' }}>
            <InputRightElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputRightElement>
            <Input
              value={searchText}
              onChange={handleSearchTextChange}
              name="query"
              placeholder="Search by name"
            />
          </InputGroup>
        </Flex>

        <Divider mt={4} />

        <DataTable
          theme={useColorModeValue('default', 'dark')}
          progressPending={status === ReducerStatus.LOADING}
          columns={completionReportColumns}
          data={filteredReports}
          onSelectedRowsChange={({ selectedRows }) =>
            setSelectedData(selectedRows)
          }
          progressComponent={
            <Center my={10}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Center>
          }
          selectableRows
          pagination
        />
      </Box>
    </Box>
  );
}

export default ReportPage;
